/* import __COLOCATED_TEMPLATE__ from './enable-user-verification.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { type PulseAccordion } from 'glint/pulse';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import { saveAs } from 'file-saver';
// @ts-ignore
import { icInterfaceIcon } from '@intercom/pulse/helpers/ic-interface-icon';
import { restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
interface Signature {
  Args: {
    accordion: PulseAccordion;
    onContinue: () => void;
  };
}
export default class EnableUserVerification extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;

  get salesforceConfig() {
    return this.finStandaloneService.salesforcePlatform.salesforceConfig!;
  }

  get salesforceInAppMessagingConfig() {
    return this.finStandaloneService.salesforcePlatform.salesforceInAppMessagingConfig!;
  }

  get isOptional() {
    return !this.salesforceInAppMessagingConfig?.isConnected;
  }

  get isComplete() {
    return Boolean(this.salesforceInAppMessagingConfig?.userVerificationEnabled);
  }

  get isDisabled() {
    return !this.salesforceConfig?.isConnected;
  }

  get instructions() {
    return [
      {
        label: this.intl.t(
          'standalone.salesforce.setup.enable-user-verification.instructions.step-1',
          {
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.salesforce.setup.enable-user-verification.instructions.step-2',
          {
            openUrl: this.salesforceConfig.buildLightningSetupUrl('Authorization/home'), // eslint-disable-line @intercom/intercom/no-bare-strings
            icon: icInterfaceIcon(['new-window'], {}),
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.salesforce.setup.enable-user-verification.instructions.step-3',
          {
            openUrl: this.salesforceConfig.buildLightningSetupUrl('Authorization/home'), // eslint-disable-line @intercom/intercom/no-bare-strings
            icon: icInterfaceIcon(['new-window'], {}),
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.salesforce.setup.enable-user-verification.instructions.step-4',
          {
            openUrl: this.salesforceConfig.buildLightningSetupUrl('LiveMessageSetup/home'),
            icon: icInterfaceIcon(['new-window'], {}),
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.salesforce.setup.enable-user-verification.instructions.step-5',
          {
            openUrl: this.salesforceConfig.buildLightningSetupUrl('LiveMessageSetup/home'),
            icon: icInterfaceIcon(['new-window'], {}),
            htmlSafe: true,
          },
        ),
      },
    ];
  }

  @restartableTask
  *verifyUserVerificationEnabled(): TaskGenerator<void> {
    try {
      yield this.salesforceInAppMessagingConfig.reload();

      if (this.salesforceInAppMessagingConfig.userVerificationEnabled) {
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.salesforce.setup.enable-user-verification.verify-success'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('standalone.salesforce.setup.enable-user-verification.verify-error'),
        );
      }
    } catch (error) {
      this.notificationsService.notifyResponseError(error);
    }
  }

  @action
  downloadJwk() {
    saveAs(new Blob([JSON.stringify(this.salesforceInAppMessagingConfig?.jwk)]), 'fin-ai-jwk.json');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Messenger::EnableUserVerification': typeof EnableUserVerification;
    'standalone/deploy/salesforce/messenger/enable-user-verification': typeof EnableUserVerification;
  }
}
